import { Brain } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

const MI_OPTIONS = [
  "Uopšte ne",
  "Malo",
  "Ponekad",
  "Prilično",
  "Veoma mnogo",
];

const MI_SCORES = [0, 1, 2, 3, 4];

export const testInteligencije: QuizConfig = {
  id: "test-inteligencije",
  illustration: {
    src: "/images/quizzes/test-inteligencije.webp",
    alt: "Muškarac istražuje rešenje dok sklapa mali drveni model.",
  },
  lastModified: "2026-10-05",
  title: "Test inteligencije: Otkrij koji tip je tvoja snaga",
  shortTitle: "Test inteligencije",
  description:
    "Besplatan test inteligencije sa rezultatima odmah. Otkrij svojih 8 tipova inteligencije i kognitivne snage za 3 minuta, bez registracije.",
  longDescription:
    "12 pitanja o tvojim navikama i sklonostima kroz teoriju višestrukih inteligencija. Umesto IQ broja, dobijaš mapu svojih kognitivnih snaga, od logičkog razmišljanja i verbalne sposobnosti do muzičke osetljivosti i razumevanja drugih ljudi.",
  icon: Brain,
  duration: "3-5 min",
  completions: 0,
  category: "Ličnost i emocije",
  focusAreasWanted: ["self_development", "identity", "emotional_regulation"],
  featured: false,
  promptExtension: `
    This is a Multiple Intelligences assessment based on Howard Gardner's theory.
    Map each question to the corresponding intelligence type. Use these exact
    Serbian keys in the scores object (never the English theory names):
    - Q1: verbalno-lingvistička
    - Q2: logičko-matematička
    - Q3: vizuelno-prostorna
    - Q4: muzička
    - Q5: telesno-kinestetička
    - Q6: interpersonalna
    - Q7: intrapersonalna
    - Q8: naturalistička
    - Q9: Checkbox - each option maps to one intelligence type in the same order as above
    - Q10: Checkbox - each option maps to one intelligence type in the same order as above
    - Q11: Open text reflection (not scored)
    - Q12: Context question (not scored)

    For each intelligence type, calculate a raw score from 0-5 (radio score
    0-4 + checkbox 0-1), then convert it to a 0-100 percentage of that scale
    (raw / 5 * 100) before writing it into scores. Every value in scores must
    be 0-100, the same scale used across all quizzes.
    Present results as a ranked profile showing all 8 types from strongest to developing.

    Format the results as:
    1. Top 2-3 intelligence types highlighted as "Tvoje najjače strane"
    2. Middle types as "Solidne sposobnosti"
    3. Lower types as "Potencijal za razvoj" (never frame negatively)

    For each top intelligence type, explain:
    - What it means in daily life
    - Careers/activities where it shines
    - How to develop it further

    Include a brief note about the open-text answer (Q11) if relevant.

    IMPORTANT: Frame this as a self-assessment of cognitive preferences, NOT an IQ test.
    Always mention: "Ovo nije merenje IQ-a, već pregled tvojih kognitivnih preferencija i snaga."
    Recommend professional assessment for clinical purposes.
    Use Serbian Latin script, "ti" form, conversational tone.
  `,
  questions: [
    // Q1: Linguistic intelligence
    {
      type: "radio",
      question: "Koliko uživaš u čitanju, pisanju i igri rečima?",
      options: MI_OPTIONS,
      scores: MI_SCORES,
    },
    // Q2: Logical-Mathematical intelligence
    {
      type: "radio",
      question:
        "Koliko lako rešavaš logičke probleme, zagonetke ili matematičke zadatke?",
      options: [
        "Teško mi ide",
        "Ponekad se snađem",
        "Prosečno",
        "Prilično lako",
        "Vrlo lako",
      ],
      scores: MI_SCORES,
    },
    // Q3: Visual-Spatial intelligence
    {
      type: "radio",
      question:
        "Koliko lako zamišljaš stvari u prostoru ili vizualizuješ ideje?",
      options: [
        "Teško mi je",
        "Slabije",
        "Prosečno",
        "Prilično lako",
        "Vrlo lako",
      ],
      scores: MI_SCORES,
    },
    // Q4: Musical intelligence
    {
      type: "radio",
      question: "Koliko si osetljiv/a na melodije, ritam i zvukove oko sebe?",
      options: MI_OPTIONS,
      scores: MI_SCORES,
    },
    // Q5: Bodily-Kinesthetic intelligence
    {
      type: "radio",
      question:
        "Koliko ti je lako da naučiš nove pokrete, sport ili ručni rad?",
      options: [
        "Teško mi ide",
        "Slabije",
        "Prosečno",
        "Prilično lako",
        "Vrlo lako",
      ],
      scores: MI_SCORES,
    },
    // Q6: Interpersonal intelligence
    {
      type: "radio",
      question: "Koliko lako čitaš emocije i namere drugih ljudi?",
      options: [
        "Teško mi je",
        "Ponekad",
        "Prosečno",
        "Prilično lako",
        "Vrlo lako",
      ],
      scores: MI_SCORES,
    },
    // Q7: Intrapersonal intelligence
    {
      type: "radio",
      question:
        "Koliko dobro razumeš sopstvene emocije i unutrašnju motivaciju?",
      options: ["Slabo", "Donekle", "Prosečno", "Dobro", "Vrlo dobro"],
      scores: MI_SCORES,
    },
    // Q8: Naturalistic intelligence
    {
      type: "radio",
      question: "Koliko primećuješ detalje i obrasce u prirodi oko sebe?",
      options: MI_OPTIONS,
      scores: MI_SCORES,
    },
    // Q9: Activity preferences (checkbox, maps to each intelligence type)
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Koje aktivnosti te najviše privlače? (označi sve što važi)",
      options: [
        "Čitanje i pisanje",
        "Rešavanje zagonetki i logičkih problema",
        "Crtanje, fotografija ili dizajn",
        "Slušanje ili pravljenje muzike",
        "Sport, ples ili ručni rad",
        "Razgovor i rad sa ljudima",
        "Meditacija i samorefleksija",
        "Boravak u prirodi",
      ],
      scores: [1, 1, 1, 1, 1, 1, 1, 1],
    },
    // Q10: Learning preferences (checkbox, maps to each intelligence type)
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Kako najlakše učiš novo gradivo? (označi sve što važi)",
      options: [
        "Čitajući tekstove i praveći beleške",
        "Analizirajući podatke i tražeći logiku",
        "Gledajući dijagrame, slike i video",
        "Slušajući predavanja ili podkaste",
        "Praktičnim radom i eksperimentisanjem",
        "Diskutujući sa drugima u grupi",
        "Povezujući gradivo sa ličnim iskustvom",
        "Učeći na primerima iz svakodnevnog života",
      ],
      scores: [1, 1, 1, 1, 1, 1, 1, 1],
    },
    // Q11: Open reflection
    {
      type: "textarea",
      question:
        "U čemu smatraš da si najinteligentniji/a? Koja veština ti najlakše dolazi?",
    },
    // Q12: Context (no scoring)
    {
      type: "radio",
      question: "Šta te motivisalo da uradiš ovaj test?",
      options: [
        "Radoznalost",
        "Lični razvoj",
        "Potrebe za posao ili školu",
        "Neko mi je preporučio",
        "Zabava",
      ],
      scores: [0, 0, 0, 0, 0],
    },
  ],
  copy: {
    title: "Tvoj profil inteligencije",
    subtitle: "Pregled tvojih kognitivnih snaga kroz 8 tipova inteligencije",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Kako da iskoristiš svoje snage",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
  landing: {
    summary:
      "Inteligencija nije samo jedan broj. Svako od nas ima jedinstvenu kombinaciju kognitivnih snaga. Ovaj test mapira tvoj profil kroz 8 tipova inteligencije i pomaže ti da prepoznaš gde leži tvoj prirodni potencijal.",
    highlights: [
      {
        title: "8 tipova inteligencije",
        description:
          "Analiziramo tvoj profil od logičkog razmišljanja do muzičke osetljivosti.",
      },
      {
        title: "Personalizovani rezultati",
        description:
          "Dobijaš konkretne savete kako da iskoristiš svoje najjače strane.",
      },
      {
        title: "Naučno zasnovano",
        description:
          "Test je inspirisan Gardnerovom teorijom višestrukih inteligencija.",
      },
    ],
    whoIsItFor:
      "Za svakoga ko želi da bolje razume svoj način razmišljanja, učenja i rešavanja problema. Posebno koristan ako razmišljaš o karijeri, obrazovanju ili ličnom razvoju.",
    whatYouLearn:
      "Saznaćeš koje su tvoje najjače kognitivne sposobnosti, kako najbolje učiš i u kojim oblastima imaš prirodni potencijal koji možda ne koristiš dovoljno.",
    howItWorks:
      "Test sadrži 12 pitanja o tvojim preferencijama, navikama i sposobnostima. Na osnovu odgovora, AI generiše personalizovan profil sa konkretnim savetima.",
    nextSteps:
      "Nakon testa, možeš da razgovaraš sa terapeutom o tome kako da bolje iskoristiš svoje kognitivne snage u svakodnevnom životu.",
    faq: [
      {
        question: "Da li je ovo IQ test?",
        answer:
          "Ne, ovo nije klasičan IQ test. Umesto jednog broja, dobijaš profil svojih kognitivnih snaga kroz 8 tipova inteligencije. Za merenje IQ-a potreban je standardizovani test kod psihologa.",
      },
      {
        question: "Na čemu se zasniva ovaj test?",
        answer:
          "Test je inspirisan teorijom višestrukih inteligencija Hauarda Gardnera, koja prepoznaje 8 različitih tipova inteligencije. Rezultati se zasnivaju na samoproceni.",
      },
      {
        question: "Koliko su rezultati pouzdani?",
        answer:
          "Rezultati odražavaju tvoju samoprocenu i služe kao polazna tačka za razumevanje sebe. Za detaljniju procenu kognitivnih sposobnosti, preporučujemo razgovor sa psihologom.",
      },
    ],
    ctaLabel: "Započni test inteligencije",
  },
};
